/**
 * Static site facts for Varano Construction.
 *
 * This is the single source of truth for the company's name/address/phone
 * (NAP), services, and primary nav. The CMS only manages *photos* (see
 * lib/siteConfig.ts) — all copy lives here and in the page components.
 */

import { Bath, HousePlus } from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import { CarpentryIcon, KitchenRangeIcon, PergolaIcon, WindowDoorIcon } from "./icons";
import type { ImageSlot } from "./siteConfig";

/** A line icon — lucide icons and our custom service icons both satisfy this. */
export type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export const SITE = {
  name: "Varano Construction",
  tagline:
    "Quality remodeling and custom construction in West Chester and Chester County.",
  phone: "610-431-7190",
  phoneHref: "tel:+16104317190",
  email: "sales@varanoconstruction.com",
  emailHref: "mailto:sales@varanoconstruction.com",
  // Service-area business — only city/state are published (footer/contact +
  // JSON-LD). No street address is kept here: SITE ships in the client bundle,
  // so a street/ZIP would be publicly retrievable from the page source.
  address: {
    city: "West Chester",
    state: "PA",
  },
  hours: "Mon–Fri 7:00AM – 5:00PM",
  license: "PA #048294",
  serviceArea: "Proudly serving Chester County and the surrounding areas.",
  founded: 1990,
} as const;

export interface Service {
  slug: string;
  title: string;
  blurb: string;
  icon: IconComponent;
  imageSlot: ImageSlot;
}

export const SERVICES: Service[] = [
  {
    slug: "home-additions",
    title: "Home Additions",
    blurb: "Seamless additions that blend with your home.",
    icon: HousePlus,
    imageSlot: "serviceAdditions",
  },
  {
    slug: "kitchen-remodeling",
    title: "Kitchen Remodeling",
    blurb: "Custom kitchens built for how you live.",
    icon: KitchenRangeIcon,
    imageSlot: "serviceKitchen",
  },
  {
    slug: "bathroom-remodeling",
    title: "Bathroom Remodeling",
    blurb: "Beautiful, functional bathrooms.",
    icon: Bath,
    imageSlot: "serviceBathroom",
  },
  {
    slug: "decks",
    title: "Decks",
    blurb: "Enjoy the outdoors in comfort and style.",
    icon: PergolaIcon,
    imageSlot: "serviceDecks",
  },
  {
    slug: "windows-and-doors",
    title: "Windows & Doors",
    blurb: "Energy-efficient windows and doors, expertly installed.",
    icon: WindowDoorIcon,
    imageSlot: "serviceWindowsDoors",
  },
  {
    slug: "custom-carpentry",
    title: "Custom Carpentry",
    blurb: "Custom trim, built-ins, and finish carpentry.",
    icon: CarpentryIcon,
    imageSlot: "serviceCustomCarpentry",
  },
];

export interface NavItem {
  label: string;
  href: string;
}

export const NAV: NavItem[] = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about/" },
  { label: "Services", href: "/services/" },
  { label: "Projects", href: "/projects/" },
  { label: "Contact", href: "/contact/" },
];

/** Project Type options for the contact form's dropdown. */
export const PROJECT_TYPES: string[] = [
  ...SERVICES.map((s) => s.title),
  "Other / Not sure yet",
];
